import type { ModelOption } from "@/lib/model-registry";

function modelIdentity(model: ModelOption) {
  const parts = model.value.toLowerCase().split("/");
  const id = parts
    .pop()!
    .replace(/\[[^\]]+\]$/, "")
    .replace(/-(?:\d{8}|20\d{2}-\d{2}-\d{2})$/, "")
    .replace(/-(?:latest|preview)$/, "");
  const claude = /^claude-([a-z]+)-(\d+(?:[.-]\d+)*)$/.exec(id);
  const legacyClaude = /^claude-(\d+(?:-\d+)*)-([a-z]+)$/.exec(id);
  // A hyphen can separate version numbers, but a size such as 70b is a
  // real variant, not a minor version (llama-3-70b).
  const generic = /^(.*?)(\d+(?:\.\d+|-\d+(?=-|$))*)(.*)$/.exec(id);
  const sizeFirst = generic && /^b(?:[-_.]|$)/.test(generic[3]!);
  const family = claude
    ? `claude-${claude[1]}`
    : legacyClaude
      ? `claude-${legacyClaude[2]}`
      : sizeFirst
        ? id
        : generic
          ? `${generic[1]}${generic[3]}`
          : id;
  const version = claude?.[2] ?? legacyClaude?.[1] ?? generic?.[2];
  // Some IDs encode a release month: mistral-large-2512 is labeled Large 3.
  // Use that reported model version, keeping the date out of the ordering.
  const dateVersion = version && /^(?:2\d|3\d)(?:0[1-9]|1[0-2])$/.test(version);
  const reportedVersion = model.label.match(/\d+(?:\.\d+)*/)?.[0];
  const numbers = dateVersion
    ? reportedVersion && reportedVersion !== version
      ? reportedVersion.split(".").map(Number)
      : [0, Number(version)]
    : (version?.split(/[.-]/).map(Number) ?? []);
  return {
    // The same model on different plans, or with different billing, is a
    // distinct choice. Numeric sizes and real tiers remain in the family.
    family: `${model.provider ?? parts.join("/")}:${model.free === true}:${family.replace(/[-_.\s]/g, "")}`,
    version: numbers,
  };
}

/** Numeric model versions descend; dates and context suffixes are not versions. */
export function compareModelVersions(a: ModelOption, b: ModelOption): number {
  const left = modelIdentity(a).version;
  const right = modelIdentity(b).version;
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const difference = (right[i] ?? 0) - (left[i] ?? 0);
    if (difference) return difference;
  }
  const labelOrder = a.label.localeCompare(b.label, undefined, {
    numeric: true,
  });
  if (labelOrder) return labelOrder;
  // Prefer the ordinary ID over an equivalent dated/context variant.
  return a.value.length - b.value.length || b.value.localeCompare(a.value);
}

/** Presentation only: the complete catalog remains available for execution. */
export function latestModelOptions(
  models: readonly ModelOption[],
): ModelOption[] {
  const latest = new Map<string, ModelOption>();
  for (const model of models) {
    const family = modelIdentity(model).family;
    const previous = latest.get(family);
    if (!previous || compareModelVersions(model, previous) < 0) {
      latest.set(family, model);
    }
  }
  const ids = new Set([...latest.values()].map((model) => model.value));
  return models.filter((model) => ids.has(model.value));
}
